<template>
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <!-- Tree -->
    <div class="lg:col-span-2 bg-white rounded-md border border-gray-200">
      <div class="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search areas, zones, or points"
          class="flex-1 rounded-md border-gray-300 bg-white px-3 py-2 text-sm transition-colors focus:border-blue-500 focus:ring-blue-500"
        />
        <button
          type="button"
          class="inline-flex min-w-20 sm:min-w-40 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          @click="clearSelection"
        >
          Clear
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          @click="toggleAllExpanded"
        >
          {{ allExpanded ? 'Collapse all' : 'Expand all' }}
        </button>
      </div>

      <div
        v-if="summary.meters > 0"
        class="flex items-center justify-between gap-3 px-4 h-14 border-b border-gray-100 bg-blue-50"
      >
        <span class="text-sm font-medium text-blue-900">
          {{ summary.meters }} {{ summary.meters === 1 ? 'point' : 'points' }} selected
        </span>
      </div>

      <div v-if="loading" class="flex min-h-[20rem] items-center justify-center p-8">
        <div class="flex animate-pulse flex-col items-center justify-center space-y-0.5">
          <span class="lnr icon-hourglass !text-4xl !text-blue-500 mb-2 inline-block w-8 text-center"></span>
          <span class="text-base font-medium">Loading Data</span>
          <span class="text-sm text-gray-500">Just a moment…</span>
        </div>
      </div>

      <div v-else-if="filteredTree.length === 0" class="flex min-h-[20rem] items-center justify-center p-8">
        <div class="flex flex-col items-center justify-center space-y-0.5">
          <span class="lnr icon-inbox !text-4xl !text-gray-500 mb-2 inline-block w-8 text-center"></span>
          <span class="text-base font-medium">{{ searchQuery.trim() ? 'No matching points' : 'No points available' }}</span>
          <span class="text-sm text-gray-500">{{ searchQuery.trim() ? 'Try a different search term.' : 'All points are already in maintenance, or none are accessible.' }}</span>
        </div>
      </div>

      <ul v-else class="divide-y divide-gray-100">
        <template v-for="(area, areaIdx) in filteredTree" :key="'a-' + area.id">
          <li :class="areaIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
            <div
              class="flex items-center gap-2 pr-4 py-4 sm:py-3 pl-2 sm:px-4 font-medium text-gray-900 hover:bg-gray-100 cursor-pointer select-none"
              role="button"
              :aria-label="isAreaExpanded(area) ? 'Collapse area' : 'Expand area'"
              :aria-expanded="isAreaExpanded(area)"
              @click="toggleAreaExpanded(area)"
            >
              <span class="text-gray-400">
                <svg v-if="isAreaExpanded(area)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M6 9l6 6l6 -6" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M9 6l6 6l-6 6" />
                </svg>
              </span>
              <label class="m-0 inline-flex items-center gap-2 cursor-pointer" @click.stop>
                <input
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  :checked="areaState(area) === 'all'"
                  :indeterminate.prop="areaState(area) === 'some'"
                  @change="toggleArea(area, $event.target.checked)"
                />
                {{ area.name }}
                <span class="text-xs font-normal text-gray-500">({{ areaMeterCount(area) }})</span>
              </label>
            </div>
          </li>

          <template v-if="isAreaExpanded(area)">
            <template v-for="(zone, zoneIdx) in area.zones" :key="'z-' + zone.id">
              <li :class="zoneIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                <div
                  class="flex items-center gap-2 pr-4 py-4 sm:py-3 pl-4 sm:pl-10 text-gray-800 hover:bg-gray-100 cursor-pointer select-none"
                  role="button"
                  :aria-label="isZoneExpanded(zone) ? 'Collapse zone' : 'Expand zone'"
                  :aria-expanded="isZoneExpanded(zone)"
                  @click="toggleZoneExpanded(zone)"
                >
                  <span class="text-gray-400">
                    <svg v-if="isZoneExpanded(zone)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M6 9l6 6l6 -6" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M9 6l6 6l-6 6" />
                    </svg>
                  </span>
                  <label class="m-0 inline-flex items-center gap-2 cursor-pointer" @click.stop>
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      :checked="zoneState(zone) === 'all'"
                      :indeterminate.prop="zoneState(zone) === 'some'"
                      @change="toggleZone(zone, $event.target.checked)"
                    />
                    {{ zone.name }}
                    <span class="text-xs text-gray-500">({{ zone.meters.length }})</span>
                  </label>
                </div>
              </li>

              <template v-if="isZoneExpanded(zone)">
                <li
                  v-for="(meter, meterIdx) in zone.meters"
                  :key="'m-' + meter.id"
                  :class="meterIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                >
                  <label class="m-0 flex items-center gap-2 pr-4 py-4 sm:py-3 pl-6 sm:pl-20 cursor-pointer text-sm text-gray-700 hover:bg-gray-100">
                    <span class="w-4 shrink-0"></span>
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      :checked="selectedMeterIds.has(meter.id)"
                      @change="toggleMeter(meter.id, $event.target.checked)"
                    />
                    {{ meter.name }}
                  </label>
                </li>
              </template>
            </template>
          </template>
        </template>
      </ul>
    </div>

    <!-- Action panel -->
    <div class="bg-white rounded-md border border-gray-200 p-4 self-start sticky top-4 space-y-4">
      <div>
        <div class="flex items-center justify-between">
          <p class="text-xs font-medium uppercase tracking-wider text-gray-500">Maintenance Mode</p>
          <a
            href="https://support.sonicu.com/"
            target="_blank"
            rel="noopener"
            title="Learn more about Maintenance Mode"
            class="flex items-center p-1 rounded-full border border-blue-700 text-blue-700 hover:bg-blue-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
              <path d="M12 8h.01" />
              <path d="M11 12h1v4h1" />
            </svg>
          </a>
        </div>
        <p class="mt-1 text-sm text-gray-600">Enable maintenance mode on selected points to suppress alarms for the specified duration.</p>
      </div>
      <div>
        <label class="text-xs font-medium uppercase tracking-wider text-gray-500">Duration</label>
        <div class="mt-2 grid grid-cols-5 gap-2">
          <button
            v-for="opt in [1, 4, 8, 24]"
            :key="opt"
            type="button"
            :class="['rounded-md border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500', !customSelected && durationHours === opt ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50']"
            @click="selectPreset(opt)"
          >
            {{ opt }}h
          </button>
          <button
            type="button"
            :class="['rounded-md border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500', customSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50']"
            @click="selectCustom"
          >
            Custom
          </button>
        </div>
        <div v-if="customSelected" class="mt-2 grid grid-cols-5 gap-2">
          <div class="col-start-4 col-span-2 flex flex-col items-stretch">
            <input
              :value="customDuration"
              @keydown="onCustomDurationKeydown"
              @input="onCustomDurationInput"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              class="w-full rounded-md border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <span class="mt-1 text-[11px] italic text-gray-400 text-right">hours (1–168)</span>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <toggle v-model:value="sendNotification"></toggle>
        <span class="text-sm text-gray-700">Send notification to users</span>
      </div>

      <button
        type="button"
        class="w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
        :disabled="!canSubmit"
        @click="showConfirm = true"
      >
        {{ phase === 'submitting' ? 'Applying…' : 'Apply' }}
      </button>

      <!-- Confirm modal -->
      <app-modal
        v-model:open="showConfirm"
        title="Confirm bulk change"
        primary-label="Continue"
        :primary-disabled="commentRequiredUnmet"
        v-on:submit="submit"
      >
        <p class="text-sm text-gray-700">
          You're about to enable maintenance on
          <strong>{{ summary.meters }}</strong> points for <strong>{{ durationHours }} hours</strong>.
        </p>
        <div class="mt-4 text-left">
          <label class="block text-sm font-medium leading-5 text-gray-700 mb-1" for="mm-label">Window Name</label>
          <input
            id="mm-label"
            v-model="windowLabel"
            type="text"
            maxlength="100"
            placeholder="e.g. Quarterly HVAC maintenance"
            class="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div class="mt-4 text-left">
          <label class="block text-sm font-medium leading-5 text-gray-700 mb-1" for="mm-comment">
            Comment<span v-if="requireComment" class="text-red-500 ml-0.5">*</span><span v-else> (optional)</span>
          </label>
          <textarea
            id="mm-comment"
            v-model="windowComment"
            rows="3"
            maxlength="500"
            placeholder="Additional notes for this maintenance window"
            class="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          ></textarea>
        </div>
      </app-modal>

      <div v-if="applyError" class="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="mt-0.5 shrink-0">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
          <path d="M10 10l4 4m0 -4l-4 4" />
        </svg>
        <span>{{ applyError }}</span>
      </div>

      <div v-if="results" class="space-y-2">
        <div
          v-if="results.summary.enabled"
          class="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="mt-0.5 shrink-0">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
            <path d="M9 12l2 2l4 -4" />
          </svg>
          <span><strong>Enabled:</strong> {{ results.summary.enabled }} {{ results.summary.enabled === 1 ? 'point' : 'points' }}</span>
        </div>
        <div
          v-if="results.summary.skipped"
          class="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="mt-0.5 shrink-0">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M12 9v4" />
            <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
            <path d="M12 16h.01" />
          </svg>
          <span><strong>Skipped:</strong> {{ results.summary.skipped }} {{ results.summary.skipped === 1 ? 'point' : 'points' }}</span>
        </div>
        <div
          v-if="results.summary.failed"
          class="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="mt-0.5 shrink-0">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
            <path d="M10 10l4 4m0 -4l-4 4" />
          </svg>
          <span>
            <strong>Failed:</strong> {{ results.summary.failed }} {{ results.summary.failed === 1 ? 'point' : 'points' }}
            <details v-if="failedItems.length" class="mt-1">
              <summary class="cursor-pointer text-xs">Show reasons</summary>
              <ul class="mt-1 space-y-0.5 pl-3 text-xs list-disc">
                <li v-for="r in failedItems" :key="r.meter_id">Meter #{{ r.meter_id }}: {{ r.reason }}</li>
              </ul>
            </details>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Toggle from "@/components/elements/toggle";
import AppModal from "@/components/elements/AppModal";

export default {
  name: "BulkMaintenancePanel",
  components: { Toggle, AppModal },
  props: {
    companyId: { type: String, required: true },
    tree: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    shouldNotifyDefault: { type: Boolean, default: true },
    requireComment: { type: Boolean, default: false },
  },
  emits: ["applied"],
  data() {
    return {
      selectedMeterIds: new Set(),
      expandedAreas: new Set(),
      expandedZones: new Set(),
      // Tracks single-child areas/zones the user has explicitly collapsed (so we
      // don't reopen them on every render).
      collapsedAreas: new Set(),
      collapsedZones: new Set(),
      searchQuery: "",
      phase: "select",
      durationHours: 8,
      customDuration: 12,
      customSelected: false,
      windowLabel: "",
      windowComment: "",
      results: null,
      applyError: null,
      showConfirm: false,
      allExpanded: false,
      sendNotification: this.shouldNotifyDefault,
    };
  },
  computed: {
    filteredTree() {
      // First, drop any meters already in maintenance — they belong to the bottom section.
      const enableable = this.tree.map((a) => ({
        ...a,
        zones: a.zones
          .map((z) => ({
            ...z,
            meters: z.meters.filter((m) => !m.maintenance_active),
          }))
          .filter((z) => z.meters.length > 0),
      })).filter((a) => a.zones.length > 0);

      // Then apply the search query if present.
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return enableable;
      return enableable
        .map((a) => {
          const zones = a.zones
            .map((z) => {
              const meters = z.meters.filter((m) => m.name.toLowerCase().includes(q));
              if (z.name.toLowerCase().includes(q)) return { ...z };
              return meters.length ? { ...z, meters } : null;
            })
            .filter(Boolean);
          if (a.name.toLowerCase().includes(q)) return { ...a };
          return zones.length ? { ...a, zones } : null;
        })
        .filter(Boolean);
    },
    summary() {
      let areas = 0, zones = 0, meters = 0;
      for (const a of this.filteredTree) {
        const aState = this.areaState(a);
        if (aState !== "none") areas += 1;
        for (const z of a.zones) {
          const zState = this.zoneState(z);
          if (zState !== "none") zones += 1;
          for (const m of z.meters) {
            if (this.selectedMeterIds.has(m.id)) {
              meters += 1;
            }
          }
        }
      }
      return { areas, zones, meters };
    },
    canSubmit() {
      if (this.phase !== "select") return false;
      if (this.summary.meters === 0) return false;
      return Number.isInteger(this.durationHours) && this.durationHours >= 1 && this.durationHours <= 168;
    },
    failedItems() {
      return (this.results?.results ?? []).filter((r) => r.status === "failed");
    },
    commentRequiredUnmet() {
      return this.requireComment && this.windowComment.trim() === "";
    },
  },
  methods: {
    selectPreset(opt) {
      this.durationHours = opt;
      this.customSelected = false;
    },
    selectCustom() {
      this.customSelected = true;
      this.durationHours = this.customDuration;
    },
    onCustomDurationKeydown(e) {
      // Allow navigation/edit keys; block everything that isn't a digit.
      const allowed = ["Backspace", "Delete", "Tab", "Escape", "Enter", "Home", "End", "ArrowLeft", "ArrowRight"];
      if (allowed.includes(e.key)) return;
      if (e.metaKey || e.ctrlKey) return; // copy/paste/select-all
      if (!/^[0-9]$/.test(e.key)) e.preventDefault();
    },
    onCustomDurationInput(e) {
      // Strip anything non-digit (handles paste of "12.5", "12e3", "+12", etc.).
      const digits = e.target.value.replace(/\D+/g, "");
      const clamped = digits === "" ? "" : Math.max(1, Math.min(168, parseInt(digits, 10)));
      // Sync the value back to keep the input controlled.
      e.target.value = clamped === "" ? "" : String(clamped);
      this.customDuration = clamped === "" ? 1 : clamped;
      this.durationHours = this.customDuration;
    },
    zoneState(zone) {
      const ids = zone.meters.map((m) => m.id);
      if (ids.length === 0) return "none";
      const selected = ids.filter((id) => this.selectedMeterIds.has(id)).length;
      if (selected === 0) return "none";
      if (selected === ids.length) return "all";
      return "some";
    },
    areaState(area) {
      const states = area.zones.map((z) => this.zoneState(z));
      if (states.every((s) => s === "none")) return "none";
      if (states.every((s) => s === "all")) return "all";
      return "some";
    },
    isAreaExpanded(area) {
      if (this.searchQuery.trim()) return true;
      if (this.allExpanded) return true;
      if (area.zones.length === 1) return !this.collapsedAreas.has(area.id);
      return this.expandedAreas.has(area.id);
    },
    toggleAllExpanded() {
      if (this.allExpanded) {
        this.allExpanded = false;
        this.expandedAreas = new Set();
        this.expandedZones = new Set();
        const collapsedAreas = new Set();
        const collapsedZones = new Set();
        for (const area of this.filteredTree) {
          if (area.zones.length === 1) collapsedAreas.add(area.id);
          for (const zone of area.zones) {
            if (zone.meters.length === 1) collapsedZones.add(zone.id);
          }
        }
        this.collapsedAreas = collapsedAreas;
        this.collapsedZones = collapsedZones;
      } else {
        this.allExpanded = true;
      }
    },
    toggleAreaExpanded(area) {
      if (this.allExpanded) {
        this.allExpanded = false;
        const next = new Set();
        for (const a of this.filteredTree) {
          if (a.id !== area.id) next.add(a.id);
        }
        this.expandedAreas = next;
        this.collapsedAreas = area.zones.length === 1 ? new Set([area.id]) : new Set();
        return;
      }
      if (area.zones.length === 1) {
        const next = new Set(this.collapsedAreas);
        if (next.has(area.id)) next.delete(area.id); else next.add(area.id);
        this.collapsedAreas = next;
        return;
      }
      const next = new Set(this.expandedAreas);
      if (next.has(area.id)) next.delete(area.id); else next.add(area.id);
      this.expandedAreas = next;
    },
    areaMeterCount(area) {
      let total = 0;
      for (const z of area.zones) total += z.meters.length;
      return total;
    },
    isZoneExpanded(zone) {
      if (this.searchQuery.trim()) return true;
      if (this.allExpanded) return true;
      if (zone.meters.length === 1) return !this.collapsedZones.has(zone.id);
      return this.expandedZones.has(zone.id);
    },
    toggleZoneExpanded(zone) {
      if (this.allExpanded) {
        this.allExpanded = false;
        const nextZones = new Set();
        for (const a of this.filteredTree) {
          for (const z of a.zones) {
            if (z.id !== zone.id) nextZones.add(z.id);
          }
        }
        this.expandedZones = nextZones;
        this.collapsedZones = zone.meters.length === 1 ? new Set([zone.id]) : new Set();
        return;
      }
      if (zone.meters.length === 1) {
        const next = new Set(this.collapsedZones);
        if (next.has(zone.id)) next.delete(zone.id); else next.add(zone.id);
        this.collapsedZones = next;
        return;
      }
      const next = new Set(this.expandedZones);
      if (next.has(zone.id)) next.delete(zone.id); else next.add(zone.id);
      this.expandedZones = next;
    },
    toggleMeter(id, checked) {
      const next = new Set(this.selectedMeterIds);
      if (checked) next.add(id); else next.delete(id);
      this.selectedMeterIds = next;
    },
    toggleZone(zone, checked) {
      const next = new Set(this.selectedMeterIds);
      for (const m of zone.meters) {
        if (checked) next.add(m.id); else next.delete(m.id);
      }
      this.selectedMeterIds = next;
    },
    toggleArea(area, checked) {
      const next = new Set(this.selectedMeterIds);
      for (const z of area.zones) {
        for (const m of z.meters) {
          if (checked) next.add(m.id); else next.delete(m.id);
        }
      }
      this.selectedMeterIds = next;
    },
    clearSelection() {
      this.selectedMeterIds = new Set();
      this.searchQuery = '';
    },
    submit() {
      this.showConfirm = false;
      this.phase = "submitting";

      const label = this.windowLabel.trim();
      const comment = this.windowComment.trim();
      const payload = {
        action: "enable",
        meter_ids: Array.from(this.selectedMeterIds),
        duration_hours: this.durationHours,
        send_notification: this.sendNotification,
        label: label || null,
        comment: comment || null,
      };

      axios
        .post(`/api-internal/${this.companyId}/maintenance/bulk`, payload)
        .then((r) => {
          this.results = r.data;
          this.applyError = null;
          this.phase = "select";
          this.selectedMeterIds = new Set();
          this.windowLabel = "";
          this.windowComment = "";
          this.$emit("applied");
        })
        .catch((e) => {
          this.phase = "select";
          this.applyError = e?.response?.data?.error ?? "Failed to apply changes.";
        });
    },
  },
};
</script>
